import { useCallback, useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';
import type { SearchFilters, SearchResponse } from '../utils/types';
import {
  clearResultsSnapshot,
  clearSessionState,
  getSessionStorageSafe,
  hasAnyFilter,
  loadResultsSnapshot,
  hasHistoryMarker,
  loadSessionState,
  readSearchParams,
  resolveRestoreState,
  saveSessionState,
  stripSearchParams,
  withHistoryMarker,
  withoutHistoryMarker,
  writeSearchParams,
  type RestoreDecision,
} from '../utils/searchHistory';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/** Safari throws after >100 replaceState calls / 30s — keep URL writes sparse. */
const URL_SYNC_DEBOUNCE_MS = 500;
const SCROLL_SAVE_THROTTLE_MS = 250;
/** If our own history.back() never produces a popstate, stop swallowing the next one. */
const IGNORE_POP_TIMEOUT_MS = 1000;
/** Keep re-applying a restored scrollTop (content grows as facets/pages land) for at most this long. */
const PENDING_SCROLL_MAX_MS = 3000;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/** What a Back/Forward/reload restore carries: the search plus, when the
 *  session still has them, the exact results that were on screen. */
export interface InitialRestore extends RestoreDecision {
  snapshot: { results: SearchResponse; ts: number } | null;
}

/**
 * Synchronous read of "should this page load reopen the widget?" — meant for
 * a useState initializer so the first React render is already the restored
 * widget (no host-page / empty-widget frame before results appear).
 */
export function readInitialRestore(): InitialRestore | null {
  if (typeof window === 'undefined') return null;
  const marked = hasHistoryMarker(window.history.state);
  const fromUrl = readSearchParams(window.location.search);
  if (!marked && !fromUrl) return null;
  const storage = getSessionStorageSafe();
  const decision = resolveRestoreState(fromUrl, loadSessionState(storage, window.location.pathname));
  if (!decision) return null;
  const snapshot = loadResultsSnapshot(storage, decision.query, decision.filters);
  return {
    ...decision,
    loadedCount: snapshot ? snapshot.results.products.length : decision.loadedCount,
    snapshot,
  };
}

export interface UseSearchHistoryOptions {
  /** Whether the modal/panel is currently shown. */
  isOpen: boolean;
  query: string;
  filters: SearchFilters;
  /** How many products are currently rendered (accumulated pages). */
  loadedCount: number;
  /** The restore this mount was seeded with (from readInitialRestore), if any. */
  initialRestore: InitialRestore | null;
  /**
   * Reopen the widget with this state. Called on popstate when the user
   * navigates forward/back onto a widget entry (the mount case is handled
   * synchronously through `initialRestore`).
   */
  onRestore: (state: InitialRestore) => void;
  /** The user left our history entry (Back button / swipe) — hide the UI only. */
  onExternalClose: () => void;
}

export interface UseSearchHistoryReturn {
  /** Call when the user opens the widget: pushes the widget history entry. */
  notifyOpen: () => void;
  /** Call when the user closes the widget: pops/cleans the entry and session state. */
  notifyClose: () => void;
  /** Attach to the scrollable results container (ref callback). */
  scrollContainerRef: (el: HTMLElement | null) => void;
  /** Apply a restored scrollTop once results that cover it are rendered. */
  applyPendingScroll: () => void;
}

/* ------------------------------------------------------------------ */
/*  Safe wrappers (history can throw under Safari rate limits / sandboxes) */
/* ------------------------------------------------------------------ */

function safeReplaceState(state: unknown, search: string): void {
  try {
    const url = `${window.location.pathname}${search}${window.location.hash}`;
    window.history.replaceState(state, '', url);
  } catch {
    /* best-effort */
  }
}

function safePushState(state: unknown, search: string): boolean {
  try {
    const url = `${window.location.pathname}${search}${window.location.hash}`;
    window.history.pushState(state, '', url);
    return true;
  } catch {
    return false;
  }
}

const getSessionStorage = getSessionStorageSafe;

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Makes the search widget a first-class browser-history citizen:
 *
 *  1. Opening pushes a history entry (marker in history.state). Back closes
 *     the widget instead of leaving the page; closing with X/Escape pops that
 *     entry so the stack doesn't accumulate.
 *  2. The current query/filters are mirrored into `?bsq=&bsf=` (replaceState,
 *     debounced) so the entry survives a reload and the URL is shareable.
 *  3. Scroll position + loaded page depth go to sessionStorage, flushed on
 *     pagehide (i.e. right before a result click navigates away).
 *  4. On mount, if the page was loaded on a widget entry (Back from a product
 *     page without bfcache) or with `bsq` in the URL, the widget reopens with
 *     the saved state via `onRestore`.
 */
export function useSearchHistory(opts: UseSearchHistoryOptions): UseSearchHistoryReturn {
  const { isOpen, query, filters, loadedCount, initialRestore, onRestore, onExternalClose } = opts;

  /* Latest values for event handlers without re-subscribing. */
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const stateRef = useRef({ query, filters, loadedCount });
  stateRef.current = { query, filters, loadedCount };
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const onExternalCloseRef = useRef(onExternalClose);
  onExternalCloseRef.current = onExternalClose;

  /* True when the CURRENT history entry was pushed by this document, so
   * history.back() is a cheap same-document traversal. False when the entry
   * was inherited from a previous document (Back from a product page reloaded
   * the host page): then back() would be a full cross-document load, so we
   * clean the entry in place with replaceState instead. */
  const pushedByUsRef = useRef(false);
  /* When we started trying to apply a restored scrollTop (0 = not trying). The
   * container is often too short on the first commit (catalog facets and
   * lazy sections land later), so we retry per frame, time-bounded. */
  const pendingSinceRef = useRef(0);
  const scrollRafRef = useRef<number | null>(null);
  /* Set right before our own history.back() so the resulting popstate isn't
   * treated as a user navigation. */
  const ignorePopRef = useRef(false);
  const ignorePopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollElRef = useRef<HTMLElement | null>(null);
  const pendingScrollRef = useRef<number | null>(
    initialRestore && initialRestore.scrollTop > 0 ? initialRestore.scrollTop : null,
  );
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentPath = () => window.location.pathname;

  const persistSession = useCallback(() => {
    if (!isOpenRef.current) return;
    const { query: q, filters: f, loadedCount: n } = stateRef.current;
    if (!q.trim() && !hasAnyFilter(f)) {
      clearSessionState(getSessionStorage());
      return;
    }
    saveSessionState(getSessionStorage(), {
      query: q,
      filters: f,
      path: currentPath(),
      loadedCount: n,
      // While a restore is still in flight, keep the target rather than the
      // clamped intermediate position.
      scrollTop: pendingScrollRef.current ?? scrollElRef.current?.scrollTop ?? 0,
    });
  }, []);

  const restoreFromCurrentEntry = useCallback((inheritedEntry: boolean): boolean => {
    const decision = readInitialRestore();
    if (!decision) return false;
    pushedByUsRef.current = !inheritedEntry;
    pendingScrollRef.current = decision.scrollTop > 0 ? decision.scrollTop : null;
    pendingSinceRef.current = 0;
    onRestoreRef.current(decision);
    return true;
  }, []);

  /* ---- 4. Mount: the restore itself already happened synchronously via
   * `initialRestore`; here we only book-keep, or scrub a stale marker. ---- */
  useEffect(() => {
    if (initialRestore) {
      pushedByUsRef.current = false; // entry inherited from the previous document
      // A shared link has no marker yet. Adopt the entry, or filter changes never
      // reach the URL, Back loses them and closing leaves ?bsq= behind.
      if (!hasHistoryMarker(window.history.state)) {
        safeReplaceState(withHistoryMarker(window.history.state), window.location.search);
      }
      return;
    }
    if (hasHistoryMarker(window.history.state)) {
      // Marker with nothing to restore: scrub it so Back behaves normally.
      safeReplaceState(withoutHistoryMarker(window.history.state), stripSearchParams(window.location.search));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---- 1. popstate: Back closes, Forward/Back-onto-entry reopens ---- */
  useEffect(() => {
    const onPop = (e: PopStateEvent) => {
      if (ignorePopRef.current) {
        ignorePopRef.current = false;
        if (ignorePopTimerRef.current) clearTimeout(ignorePopTimerRef.current);
        return;
      }
      if (hasHistoryMarker(e.state)) {
        if (!isOpenRef.current) restoreFromCurrentEntry(false);
      } else if (isOpenRef.current) {
        pushedByUsRef.current = false;
        onExternalCloseRef.current();
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [restoreFromCurrentEntry]);

  /* ---- 2. Mirror query/filters into the URL + session (debounced) ---- */
  const debouncedQuery = useDebounce(query, URL_SYNC_DEBOUNCE_MS);
  useEffect(() => {
    if (!isOpen) return;
    // Only touch entries that are ours; never rewrite the host page's own URL
    // state if the marker got lost (e.g. the host router replaced the entry).
    if (!hasHistoryMarker(window.history.state)) return;
    safeReplaceState(
      withHistoryMarker(window.history.state),
      writeSearchParams(window.location.search, { query: debouncedQuery, filters }),
    );
    persistSession();
  }, [isOpen, debouncedQuery, filters, loadedCount, persistSession]);

  /* ---- 3. Flush session state right before the page is left ---- */
  useEffect(() => {
    const onPageHide = () => persistSession();
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [persistSession]);

  /* Throttled scroll → session */
  const onScroll = useCallback(() => {
    if (scrollTimerRef.current) return;
    scrollTimerRef.current = setTimeout(() => {
      scrollTimerRef.current = null;
      persistSession();
    }, SCROLL_SAVE_THROTTLE_MS);
  }, [persistSession]);

  /* The user took over scrolling: stop steering toward the restored offset. */
  const cancelPendingScroll = useCallback(() => {
    pendingScrollRef.current = null;
    pendingSinceRef.current = 0;
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  const scrollContainerRef = useCallback((el: HTMLElement | null) => {
    const prev = scrollElRef.current;
    if (prev === el) return;
    if (prev) {
      prev.removeEventListener('scroll', onScroll);
      prev.removeEventListener('wheel', cancelPendingScroll);
      prev.removeEventListener('touchmove', cancelPendingScroll);
    }
    scrollElRef.current = el;
    if (el) {
      el.addEventListener('scroll', onScroll, { passive: true });
      el.addEventListener('wheel', cancelPendingScroll, { passive: true });
      el.addEventListener('touchmove', cancelPendingScroll, { passive: true });
    }
  }, [onScroll, cancelPendingScroll]);

  /* Unmount cleanup for timers only. The scroll listeners are owned by the
   * ref callback above (detached when React passes it null) and must NOT be
   * removed here: StrictMode runs this cleanup on its simulated unmount while
   * React 18 does not re-invoke ref callbacks, so removing them here would
   * silently leave the container without listeners in development. Every
   * handle is reset, not just cleared, for the same reason. */
  useEffect(() => () => {
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }
    if (ignorePopTimerRef.current) {
      clearTimeout(ignorePopTimerRef.current);
      ignorePopTimerRef.current = null;
    }
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  /** Call from a useLayoutEffect (after the results committed, before paint)
   *  so the restored offset is never visible as a jump. If the container is
   *  still too short (facets / further sections land after the first commit)
   *  it keeps re-applying once per frame until the target is reached, the
   *  user scrolls, or PENDING_SCROLL_MAX_MS elapse. */
  const applyPendingScroll = useCallback(() => {
    const top = pendingScrollRef.current;
    const el = scrollElRef.current;
    if (top === null || !el) return;
    if (!pendingSinceRef.current) pendingSinceRef.current = performance.now();
    el.scrollTop = top;
    const reached = el.scrollTop >= top - 1;
    const timedOut = performance.now() - pendingSinceRef.current > PENDING_SCROLL_MAX_MS;
    if (reached || timedOut) {
      pendingScrollRef.current = null;
      pendingSinceRef.current = 0;
      return;
    }
    if (scrollRafRef.current === null) {
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        applyPendingScroll();
      });
    }
  }, []);

  /* ---- Open / close hooks for the widget ---- */

  const notifyOpen = useCallback(() => {
    if (hasHistoryMarker(window.history.state)) return; // already on our entry (restore path)
    const { query: q, filters: f } = stateRef.current;
    const ok = safePushState(
      withHistoryMarker(null),
      writeSearchParams(window.location.search, { query: q, filters: f }),
    );
    pushedByUsRef.current = ok;
  }, []);

  const notifyClose = useCallback(() => {
    clearSessionState(getSessionStorage());
    clearResultsSnapshot(getSessionStorage());
    cancelPendingScroll();
    if (!hasHistoryMarker(window.history.state)) {
      pushedByUsRef.current = false;
      return;
    }
    if (pushedByUsRef.current) {
      // Same-document entry we created: pop it so Back isn't a no-op later.
      pushedByUsRef.current = false;
      ignorePopRef.current = true;
      if (ignorePopTimerRef.current) clearTimeout(ignorePopTimerRef.current);
      ignorePopTimerRef.current = setTimeout(() => { ignorePopRef.current = false; }, IGNORE_POP_TIMEOUT_MS);
      try {
        window.history.back();
      } catch {
        ignorePopRef.current = false;
        safeReplaceState(withoutHistoryMarker(window.history.state), stripSearchParams(window.location.search));
      }
    } else {
      // Inherited entry (page reloaded onto it): clean in place, no reload.
      safeReplaceState(withoutHistoryMarker(window.history.state), stripSearchParams(window.location.search));
    }
  }, []);

  return { notifyOpen, notifyClose, scrollContainerRef, applyPendingScroll };
}
